import { FinoraApiClient } from "@finora/api-client";
import { useAuthStore } from "../store/auth-store";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3001/api";

export const api = new FinoraApiClient({
  baseUrl: API_BASE_URL,
  getAuthHeaders: (): Record<string, string> => {
    const token = useAuthStore.getState().token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  onUnauthorized: () => {
    useAuthStore.getState().logout();
  },
});

/**
 * Mali generički fetch helper (razgovor 28.09.2026) - za endpoint-e dodate
 * u ovom zadatku (dashboard from/to, kategorije artikala) koji namerno NISU
 * dodati u veliki, deljeni @finora/api-client paket (izbegava se rizik
 * ručne izmene tog fajla). Isti obrazac autentifikacije/greške kao
 * FinoraApiClient.request - Bearer token iz auth store-a, JSON telo,
 * 401 -> logout.
 */
export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers as Record<string, string> | undefined),
  };
  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    if (res.status === 401) {
      useAuthStore.getState().logout();
    }
    let message = "Došlo je do neočekivane greške. Pokušajte ponovo.";
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      // ignorišemo - koristimo generičku poruku iznad
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

/** Upload fajla (FormData) preko istog auth mehanizma - koristi se za
 * "Slika kategorije" (POS kategorija-tajl, Task 3). */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    if (res.status === 401) {
      useAuthStore.getState().logout();
    }
    let message = "Otpremanje nije uspelo.";
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      // ignorišemo - koristimo generičku poruku iznad
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}
